/**
 * `useActiveScribeSession` — interrupted-visit recovery data layer.
 *
 * Wraps the three recovery endpoints that back the "Interrupted visit found"
 * modal. All three go through the typed oRPC `useClient()` (the same client
 * every other scribe call uses) and surface failures through `logError`.
 *
 *   1. `getActiveSession(encounterId)`
 *        → GET /scribe/sessions/{encounterId}/active
 *        → `{ session: ActiveScribeSessionDto | null }`
 *        Returns the recoverable session, or null when there's nothing to
 *        recover. The caller is responsible for the isolation double-check
 *        (`session.encounterId === encounterId` && `chunkCount > 0`).
 *
 *   2. `getChunks(encounterId, sessionId)`
 *        → GET /scribe/sessions/{encounterId}/{sessionId}/chunks
 *        → `{ sessionId, chunks: ScribeChunkDto[] }`
 *        The prior transcript lines to render into the pane on Resume.
 *
 *   3. `discardSession(encounterId, sessionId)`
 *        → DELETE /scribe/sessions/{encounterId}/{sessionId}
 *        → `{ discarded: boolean }`
 *
 * No TanStack-Query caching here on purpose — the `/active` probe is a
 * one-shot gate fired imperatively at the moment the doctor requests a join
 * (before the scribe auto-starts), not a polled/cached read.
 */
import { useCallback } from 'react'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

export type ActiveScribeSession = NonNullable<
  Awaited<ReturnType<OrpcClient['scribeGetActiveSession']>>['session']
>
export type ScribeChunk = Awaited<
  ReturnType<OrpcClient['scribeGetSessionChunks']>
>['chunks'][number]

export interface UseActiveScribeSessionResult {
  /**
   * Probe for a recoverable session on the given encounter. Returns the DTO
   * or null (both the "no session" and the "request failed" cases resolve to
   * null so the caller can safely fall through to a normal fresh start —
   * recovery is best-effort and must never block the call).
   */
  getActiveSession: (encounterId: string) => Promise<ActiveScribeSession | null>
  /**
   * Same probe as {@link getActiveSession} but RETHROWS on transport failure
   * instead of swallowing it to `null`. On-mount hydration needs to tell a
   * *definitive empty* response (`{ session: null }` → return null → safe to
   * clear the marker) apart from a *failed* probe (throws → keep the marker, a
   * transient blip must not destroy the recovery pointer).
   */
  getActiveSessionStrict: (encounterId: string) => Promise<ActiveScribeSession | null>
  /** Fetch the prior transcript lines for a recovered session. */
  getChunks: (encounterId: string, sessionId: string) => Promise<ScribeChunk[]>
  /** Discard a recovered session server-side. Resolves to the discarded flag. */
  discardSession: (encounterId: string, sessionId: string) => Promise<boolean>
}

export function useActiveScribeSession(): UseActiveScribeSessionResult {
  const client = useClient()

  const getActiveSession = useCallback<UseActiveScribeSessionResult['getActiveSession']>(
    async (encounterId) => {
      try {
        const res = await client.scribeGetActiveSession({ encounterId })
        return res.session ?? null
      } catch (err) {
        // Recovery is best-effort — a failed probe must not block the join.
        logError('scribe-recovery', 'getActiveSession failed', err)
        return null
      }
    },
    [client],
  )

  const getActiveSessionStrict = useCallback<
    UseActiveScribeSessionResult['getActiveSessionStrict']
  >(
    async (encounterId) => {
      // Deliberately NOT wrapped in try/catch — a transport failure propagates
      // so the caller (on-mount hydration) can keep the marker on a blip
      // instead of clearing it on a conflated `null`.
      const res = await client.scribeGetActiveSession({ encounterId })
      return res.session ?? null
    },
    [client],
  )

  const getChunks = useCallback<UseActiveScribeSessionResult['getChunks']>(
    async (encounterId, sessionId) => {
      const res = await client.scribeGetSessionChunks({ encounterId, sessionId })
      return res.chunks ?? []
    },
    [client],
  )

  const discardSession = useCallback<UseActiveScribeSessionResult['discardSession']>(
    async (encounterId, sessionId) => {
      const res = await client.scribeDiscardSession({ encounterId, sessionId })
      return Boolean(res.discarded)
    },
    [client],
  )

  return { getActiveSession, getActiveSessionStrict, getChunks, discardSession }
}
